import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminLiquidSurface from "../components/AdminLiquidSurface"
import AdminPageHeader from "../components/AdminPageHeader"
import DeploysList from "./DeploysList"
import FeaturesCard from "./FeaturesCard"
import HealthSection from "./HealthSection"
import TrendTile from "./TrendTile"
import WarningsList from "./WarningsList"
import {
  bannerMessage,
  bannerStatus,
  formatMs,
  formatNumber,
  formatPercent,
  formatPercentValue,
  seriesPoints,
  statusMeta,
} from "./statusUtils"

// Chart colours: brand purple for volume, red for errors, amber for slowness.
const TREND_COLORS = {
  requests: "#7C3AED",
  errors: "#D92D20",
  latency: "#F79009",
  cpu: "#0E9384",
}

const REFRESH_INTERVAL_MS = 60 * 1000
const SLOW_LINE_MS = 3000
const DB_CPU_LINE = 85

function StatusChip({ status }) {
  const meta = statusMeta(status)
  return (
    <Chip
      size="small"
      label={meta.label.toUpperCase()}
      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }}
    />
  )
}

StatusChip.propTypes = { status: PropTypes.string }

function Stat({ label, value, detail, tone }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        minWidth: 0,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: tone || "text.primary" }}
      >
        {value}
      </Typography>
      {detail ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ wordBreak: "break-all" }}
        >
          {detail}
        </Typography>
      ) : null}
    </Box>
  )
}

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  detail: PropTypes.node,
  tone: PropTypes.string,
}

function probeStat(label, probe) {
  if (!probe) return <Stat label={label} value="–" detail="Not configured" />
  return (
    <Stat
      label={label}
      value={probe.ok ? formatMs(probe.latency_ms) : "Unreachable"}
      detail={`${probe.url} · ${probe.status_code ?? probe.error}`}
      tone={
        probe.ok ? statusMeta("operational").color : statusMeta("down").color
      }
    />
  )
}

function EnvironmentCard({ environment }) {
  const { probes, elastic_beanstalk: eb, recent, series } = environment
  const failing = recent.error_rate
  return (
    <AdminLiquidSurface variant="card" sx={{ p: { xs: 2, sm: 3 } }}>
      <Column gap={2.5}>
        <Row sx={{ alignItems: "center", gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {environment.label}
          </Typography>
          <StatusChip status={environment.status} />
        </Row>

        {environment.aws_errors.length ? (
          <Alert severity="warning">
            Couldn&apos;t read some AWS data (
            {environment.aws_errors.join(", ")}
            ). The status above may be incomplete.
          </Alert>
        ) : null}

        <HealthSection elasticBeanstalk={eb} />

        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          {probeStat("API right now", probes.api)}
          {probeStat("Web app right now", probes.web)}
        </Box>

        <Column gap={1}>
          <Typography variant="overline" color="text.secondary">
            Last 15 minutes
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            }}
          >
            <Stat label="Requests" value={recent.requests ?? "–"} />
            <Stat
              label="Failing"
              value={formatPercent(recent.requests === null ? null : failing)}
              detail={`${recent.server_errors ?? "–"} server errors`}
            />
            <Stat
              label="Slowest 5%"
              value={formatMs(recent.slowest_5pct_avg_ms)}
              detail="average"
            />
            <Stat
              label="Database CPU"
              value={recent.db_cpu === null ? "–" : `${recent.db_cpu}%`}
              detail={`${recent.db_connections ?? "–"} connections`}
            />
          </Box>
        </Column>

        <Column gap={1}>
          <Typography variant="overline" color="text.secondary">
            Last 24 hours · 5-minute steps
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TrendTile
              title="Requests"
              points={seriesPoints(series, "requests")}
              color={TREND_COLORS.requests}
              format={formatNumber}
            />
            <TrendTile
              title="Server errors"
              points={seriesPoints(series, "errors")}
              color={TREND_COLORS.errors}
              format={formatNumber}
              alertWhenPositive
            />
            <TrendTile
              title="Slowest 5% of responses"
              points={seriesPoints(series, "latency_p95", 1000)}
              color={TREND_COLORS.latency}
              format={formatMs}
              limit={SLOW_LINE_MS}
              limitLabel={`alert at ${SLOW_LINE_MS / 1000} s`}
            />
            <TrendTile
              title="Database CPU"
              points={seriesPoints(series, "db_cpu")}
              color={TREND_COLORS.cpu}
              format={formatPercentValue}
              limit={DB_CPU_LINE}
              limitLabel={`alert at ${DB_CPU_LINE}%`}
            />
          </Box>
        </Column>

        <DeploysList deploys={environment.deploys} />
        <WarningsList warnings={eb?.warnings} />
      </Column>
    </AdminLiquidSurface>
  )
}

EnvironmentCard.propTypes = { environment: PropTypes.object.isRequired }

export default function AdminSystemStatus() {
  const [snapshot, setSnapshot] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback((refresh = false) => {
    setLoading(true)
    return adminApi
      .fetchSystemStatus(refresh ? { refresh: 1 } : undefined)
      .then(({ data }) => {
        setSnapshot(data)
        setError(null)
      })
      .catch(() => setError("Could not load the system status."))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(), REFRESH_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [load])

  const banner = statusMeta(bannerStatus(snapshot))

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="System status"
        subtitle={
          snapshot
            ? `Checked ${new Date(
                snapshot.checked_at
              ).toLocaleTimeString()} · refreshes every minute`
            : "Production and dev health from AWS."
        }
        actions={
          <Button
            variant="contained"
            onClick={() => load(true)}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      <FeaturesCard />

      <Box
        sx={{
          px: 3,
          py: 2,
          borderRadius: 2,
          bgcolor: banner.bg,
          color: banner.color,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {bannerMessage(snapshot)}
        </Typography>
      </Box>

      {snapshot && !snapshot.environments.length ? (
        <Alert severity="info">No environments are configured.</Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          alignItems: "start",
        }}
      >
        {(snapshot?.environments || []).map((environment) => (
          <EnvironmentCard key={environment.key} environment={environment} />
        ))}
      </Box>
    </Column>
  )
}
